import React from 'react'
import {
  Box,
  Button,
  Typography,
  IconButton
} from '@mui/material'
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material'

export interface PaginationProps {
  currentPage: number
  totalPages: number
  totalItems: number
  itemsPerPage: number
  onPageChange: (page: number) => void
  showResultsSummary?: boolean
  resultsLabel?: string
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  showResultsSummary = true,
  resultsLabel = 'Featured Listings'
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)

  const generatePageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Always show first page
      pages.push(1)

      if (currentPage > 3) {
        pages.push('...')
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1)
      const end = Math.min(totalPages - 1, currentPage + 1)

      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i)
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push('...')
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages)
      }
    }

    return pages
  }

  const pageNumbers = generatePageNumbers()

  const getButtonStyles = (page: number | string, isActive: boolean = false) => {
    const baseStyles = {
      minWidth: '40px',
      height: '40px',
      borderRadius: '8px',
      fontFamily: 'Montserrat, sans-serif',
      fontSize: '15px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textDecoration: 'none',
      border: '2px solid transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }

    if (isActive) {
      return {
        ...baseStyles,
        backgroundColor: '#b08b4f',
        color: '#ffffff',
        borderColor: '#b08b4f'
      }
    }

    if (page === '...') {
      return {
        ...baseStyles,
        backgroundColor: '#ffffff',
        color: '#0e2340',
        cursor: 'default'
      }
    }

    return {
      ...baseStyles,
      backgroundColor: '#ffffff',
      color: '#0e2340',
      '&:hover': {
        backgroundColor: '#b08b4f',
        color: '#ffffff',
        borderColor: '#b08b4f'
      }
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      {/* Pagination Buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {/* Previous Button */}
        <Button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          sx={{
            minWidth: '40px',
            height: '40px',
            borderRadius: '8px',
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '15px',
            fontWeight: 600,
            backgroundColor: currentPage === 1 ? '#e0e0e0' : '#0e2340',
            color: currentPage === 1 ? '#666' : '#ffffff',
            border: '2px solid #0e2340',
            '&:hover': {
              backgroundColor: currentPage === 1 ? '#e0e0e0' : '#ffffff',
              color: currentPage === 1 ? '#666' : '#0e2340',
              borderColor: '#0e2340'
            },
            '&:disabled': {
              backgroundColor: '#e0e0e0',
              color: '#666',
              borderColor: '#e0e0e0'
            }
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: '20px' }} />
        </Button>

        {/* Page Numbers */}
        {pageNumbers.map((page, index) => (
          <Button
            key={index}
            onClick={() => typeof page === 'number' && onPageChange(page)}
            disabled={page === '...'}
            sx={getButtonStyles(page, page === currentPage)}
          >
            {page}
          </Button>
        ))}

        {/* Next Button */}
        <Button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          sx={{
            minWidth: '40px',
            height: '40px',
            borderRadius: '8px',
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '15px',
            fontWeight: 600,
            backgroundColor: '#ffffff',
            color: '#0e2340',
            border: '2px solid #0e2340',
            '&:hover': {
              backgroundColor: '#0e2340',
              color: '#ffffff',
              borderColor: '#0e2340'
            },
            '&:disabled': {
              backgroundColor: '#e0e0e0',
              color: '#666',
              borderColor: '#e0e0e0'
            }
          }}
        >
          <ChevronRightIcon sx={{ fontSize: '20px' }} />
        </Button>
      </Box>

      {/* Results Summary */}
      {showResultsSummary && (
        <Typography
          sx={{
            textAlign: 'center',
            marginTop: '16px',
            color: '#64748b',
            fontSize: '14px',
            fontWeight: 500,
            fontFamily: 'Montserrat, sans-serif'
          }}
        >
          {startItem} - {endItem} of {totalItems} {resultsLabel}
        </Typography>
      )}
    </Box>
  )
}

export default Pagination
